import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Circle, Loader2, PauseCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg, orderImage, statusText } from "@/lib/api";
import { Loading, MockBadge } from "@/components/Loading";
import { stickerSrc } from "@/components/wizard/StepSticker";
import { ItemList } from "@/components/wizard/StepProducts";
import { money } from "@/lib/api";

const ORDERED = ["processing", "awaiting_approval", "approved", "in_production", "shipped"];

const buildTimeline = (o) => {
  const job = (t) => o.jobs.find((j) => j.type === t)?.status;
  if (o.test_order)
    return [
      { label: "Test order sent (no payment)", state: "done" },
      { label: "Video checked", state: job("process_video") },
      { label: "Posted to YouTube (unlisted)", state: job("youtube_upload"), mock: o.youtube?.simulated, link: o.youtube?.url, processing: o.youtube?.processing },
      { label: "QR sticker created & verified", state: job("qr_generate") },
      { label: "QR sticker emailed to you", state: o.sticker_emailed ? "done" : job("email_test_sticker") },
    ];
  const rank = ORDERED.indexOf(o.status);
  return [
    { label: o.free_order ? "Free order confirmed" : "Payment confirmed", state: o.payment_status === "paid" || o.payment_status === "refunded" ? "done" : "todo" },
    { label: "Video checked", state: job("process_video") },
    { label: "Uploaded to YouTube (unlisted)", state: job("youtube_upload"), mock: o.youtube?.simulated, link: o.youtube?.url, processing: o.youtube?.processing },
    { label: "QR codes created & verified", state: job("qr_generate") },
    { label: "Final check by our team", state: rank >= 2 ? "done" : rank === 1 ? "running" : "todo" },
    { label: "Making your keepsakes", state: rank >= 4 ? "done" : rank === 3 ? "running" : "todo", mock: o.fulfillment_simulated },
    { label: "Shipped", state: o.status === "shipped" ? "done" : "todo", mock: o.tracking?.simulated },
    { label: o.delivered_at ? `Delivered ${new Date(o.delivered_at).toLocaleDateString()}` : "Delivered", state: o.delivered_at ? "done" : "todo", mock: o.tracking?.simulated },
  ];
};

const Icon = ({ state }) => {
  if (state === "done" || state === "succeeded") return <Check className="text-white" size={20} />;
  if (state === "running" || state === "queued") return <Loader2 className="animate-spin text-[#A83232]" size={20} />;
  if (state === "failed") return <PauseCircle className="text-[#D97706]" size={20} />;
  return <Circle className="text-[#E6DEC6]" size={14} />;
};

const DeleteVideoDialog = ({ order, onDone }) => {
  const [open, setOpen] = useState(false);
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/youtube/delete`);
      toast.success("Your video was deleted from YouTube.");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); setSure(false); }}>
      <DialogTrigger asChild><button className="btn-outline w-full" data-testid="delete-video-button">Delete my video from YouTube</button></DialogTrigger>
      <DialogContent className="bg-[#FFFDF9]" data-testid="delete-video-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Delete this video from YouTube?</DialogTitle>
          <DialogDescription className="text-lg text-[#4A3B32]">The video will be <b>permanently deleted</b> from YouTube. <b>Your printed keepsakes will stop playing it.</b> This can't be undone.</DialogDescription>
        </DialogHeader>
        <label className="flex cursor-pointer gap-3 text-lg">
          <input type="checkbox" className="mt-1 h-6 w-6 shrink-0 accent-[#A83232]" checked={sure} onChange={(e) => setSure(e.target.checked)} data-testid="delete-video-confirm-checkbox" />
          I understand my QR codes will no longer play this video.
        </label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button className="btn-primary flex-1 !bg-[#991B1B]" disabled={!sure || busy} onClick={del} data-testid="delete-video-confirm-button">{busy ? "Deleting..." : "Yes, delete it"}</button>
          <button className="btn-secondary flex-1" onClick={() => setOpen(false)} data-testid="delete-video-cancel-button">Keep my video</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function OrderStatus() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const load = useCallback(() => api.get(`/orders/${orderId}`).then(({ data }) => setO(data)), [orderId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);
  if (!o) return <Loading />;
  const live = ORDERED.includes(o.status) || ["on_hold", "test_complete"].includes(o.status);
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-8" data-testid="order-status-page">
      <Link to="/orders" className="text-lg font-semibold text-[#A83232] underline" data-testid="back-to-orders-link">← My keepsakes</Link>
      <h1 className="mt-4 text-4xl font-bold text-[#4A2A18]" data-testid="order-title">{o.title}</h1>
      <p className="mt-2 text-xl font-semibold text-[#5C4A3E]" data-testid="order-status-text">{statusText(o)}</p>
      {o.status === "on_hold" && <p className="mt-4 rounded-xl border-2 border-[#D97706] bg-[#FEF3C7] p-4 text-lg" data-testid="order-hold-notice">Your order is paid and safe. We hit a small snag preparing it and our team has been notified. You don't need to do anything.</p>}
      <div className="mt-10 grid gap-10 md:grid-cols-5">
        <ol className="paper-card space-y-1 p-6 md:col-span-3" data-testid="order-timeline">
          {buildTimeline(o).map((s, i) => (
            <li key={s.label} className="flex items-center gap-4 py-3" data-testid={`timeline-step-${i}`}>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${s.state === "done" || s.state === "succeeded" ? "border-[#2E6930] bg-[#2E6930]" : "border-[#E6DEC6] bg-white"}`}><Icon state={s.state} /></span>
              <span className="flex min-w-0 flex-col items-start gap-2">
                <span className="text-lg text-[#2C1E16]">{s.label}</span>
                {s.processing && <span className="text-base font-semibold text-[#B45309]" data-testid="timeline-youtube-processing">YouTube is getting your video ready. The link appears here in a minute or two.</span>}
                {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="btn-primary !min-h-0 !px-4 !py-2 !text-base" data-testid="timeline-youtube-link">Watch on YouTube</a>}
              </span>
              {s.mock && !s.link && <MockBadge testid={`timeline-mock-${i}`}>not real</MockBadge>}
            </li>
          ))}
        </ol>
        <div className="space-y-5 text-center md:col-span-2">
          {orderImage(o, stickerSrc) && <img src={orderImage(o, stickerSrc)} alt="Your keepsake" className="mx-auto w-40 rotate-[2deg] rounded-lg shadow-xl" data-testid="order-sticker-image" />}
          <div className="paper-card p-4 text-left"><ItemList order={o} /><p className="mt-3 text-right text-lg font-bold" data-testid="order-total">{o.free_order ? <>Total <s className="mr-1 font-normal text-[#8A7A6E]">{money(o.total)}</s> $0.00 (free gift)</> : <>Total {money(o.total)}</>}</p></div>
          {o.tracking && <p className="text-lg" data-testid="order-tracking">Tracking: {o.tracking.url ? <a className="font-bold text-[#A83232] underline" href={o.tracking.url} target="_blank" rel="noreferrer" data-testid="order-tracking-link">{o.tracking.number}</a> : <b>{o.tracking.number}</b>} ({o.tracking.carrier})</p>}
          {live && !o.video_removed && <a href={o.memory_url} target="_blank" rel="noreferrer" className="btn-secondary w-full" data-testid="open-memory-page-link">Open my memory page</a>}
          {o.video_removed ? <p className="text-lg text-[#991B1B]" data-testid="video-removed-notice">This video has been removed.</p>
            : live && <DeleteVideoDialog order={o} onDone={load} />}
        </div>
      </div>
    </div>
  );
}
